<script setup lang="ts">
import {
  isMine,
  isUnread,
  lastMessage,
  MANY_ORGANISATIONS,
  searchMessages,
  setNotifyMessages,
  snippetParts,
  threadAvatar,
  threadTitle,
  unreadCount,
  useMessaging,
  visibleThreads,
  type SearchHit,
  type Thread,
} from "../composables/useMessaging"
import { dateLocale, t, tn } from "../lib/i18n"

// Messages: the conversations, and the open one beside them on wide screens. Also the shop's
// inbox inside the POS (theme "pos": the POS's look). Rich content in a message: the
// message-body slot, passed on to HubThreadView.
const props = withDefaults(
  defineProps<{
    /** Under the title, e.g. the shop's name in the POS. */
    subtitle?: string
    /** Order conversations get "Open order", which emits open-order with the order id. */
    showOpenOrder?: boolean
    theme?: "app" | "pos"
  }>(),
  { subtitle: "", showOpenOrder: false, theme: "app" },
)
const emit = defineEmits<{ "open-order": [orderId: string] }>()
provide("hubTheme", props.theme)

const hub = useMessaging()
const showNew = ref(false)
const searching = ref(false)
const query = ref("")
const searchInput = ref<HTMLInputElement | null>(null)

/** Chips to show one organisation at a time, for people in more than one. */
const orgChips = computed(() => ((hub.me?.organisations.length ?? 0) > 1 ? hub.me!.organisations : []))
/** Too many for chips: one button and a search (HubOrgPicker) */
const manyOrgs = computed(() => orgChips.value.length > MANY_ORGANISATIONS)
const picking = ref(false)
/** The organisation shown, when one is */
const filterOrg = computed(() => (hub.orgFilter ? (hub.me?.organisations.find((o) => o.id === hub.orgFilter) ?? null) : null))

function pickOrg(id: string | null) {
  hub.orgFilter = id
  picking.value = false
}

const bellFailed = ref(false)
async function toggleNotifications() {
  const org = filterOrg.value
  if (!org) return
  bellFailed.value = false
  try {
    await setNotifyMessages(org.id, org.notifyMessages === false)
  } catch {
    bellFailed.value = true
  }
}
const threads = computed(() => {
  const q = query.value.trim().toLowerCase()
  const list = visibleThreads()
  if (!q) return list
  return list.filter((th) => threadTitle(th).toLowerCase().includes(q) || (lastMessage(th)?.body.toLowerCase().includes(q) ?? false))
})
/** Messages matching the search, in the conversations shown */
const hits = ref<SearchHit[]>([])
let searchTimer: ReturnType<typeof setTimeout> | undefined
watch(query, (text) => {
  clearTimeout(searchTimer)
  if (text.trim().length < 2) {
    hits.value = []
    return
  }
  searchTimer = setTimeout(async () => {
    const found = await searchMessages(text).catch(() => [])
    if (query.value === text) hits.value = found
  }, 250)
})
const shownHits = computed(() => {
  const shown = new Map(visibleThreads().map((th) => [th.id, th]))
  return hits.value.flatMap((h) => {
    const thread = shown.get(h.threadId)
    return thread ? [{ ...h, thread }] : []
  })
})

function openHit(hit: SearchHit) {
  hub.focusMessageId = hit.messageId
  hub.openThreadId = hit.threadId
}

const openThread = computed(() => hub.threads.find((th) => th.id === hub.openThreadId) ?? null)
const unsent = computed(() => hub.outbox.filter((e) => e.state !== "failed").length)

function avatarProps(thread: Thread) {
  const a = threadAvatar(thread, 44)
  return { name: a.name, id: a.id, src: a.image, photo: a.photo }
}

function preview(thread: Thread): string {
  const last = lastMessage(thread)
  if (!last) return t("No messages yet")
  // Only files: the first one's name
  const text = last.body || (last.attachments?.length ? `📎 ${last.attachments[0]!.name}` : "")
  return (isMine(thread, last) ? t("You: ") : "") + text.replace(/\s+/g, " ")
}

function time(thread: Thread): string {
  const last = lastMessage(thread)
  return last ? when(last.createdAt) : ""
}

function when(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  if (now.toDateString() === d.toDateString()) return d.toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" })
  if (now.getTime() - d.getTime() < 6 * 24 * 3600 * 1000) return d.toLocaleDateString(dateLocale(), { weekday: "short" })
  return d.toLocaleDateString(dateLocale(), { day: "numeric", month: "numeric" })
}

async function toggleSearch() {
  searching.value = !searching.value
  if (!searching.value) {
    query.value = ""
    return
  }
  await nextTick()
  searchInput.value?.focus()
}
</script>

<template>
  <!-- Laid out by its own width, not the window's: in the POS it sits in a column -->
  <div class="hub-inbox" :class="theme === 'pos' ? 'hub-ui hub-theme-pos' : ''">
    <div class="inbox" :class="{ 'thread-open': !!openThread }">
      <section class="list-pane">
        <header class="top">
          <div class="title">
            <h1>{{ t("Messages") }}</h1>
            <span v-if="subtitle">{{ subtitle }}</span>
          </div>
          <!-- The Johku team tells the shops about incidents in Johku -->
          <!-- One organisation shown: its message notifications on or off for this person -->
          <button
            v-if="filterOrg && theme === 'app'"
            type="button"
            class="hub-icon-button"
            :class="{ quiet: filterOrg.notifyMessages === false }"
            :aria-pressed="filterOrg.notifyMessages !== false"
            :aria-label="t('Notifications of new messages: {name}', { name: filterOrg.name })"
            @click="toggleNotifications"
          >
            <HubIcon :name="filterOrg.notifyMessages === false ? 'bell-off' : 'bell'" :size="22" />
          </button>
          <button v-if="hub.incidentTeam" type="button" class="hub-icon-button" :aria-label="t('Incidents')" @click="hub.openIncidentId = 'list'">
            <HubIcon name="warning" :size="22" />
          </button>
          <button type="button" class="hub-icon-button" :aria-label="t('Search')" :aria-pressed="searching" @click="toggleSearch">
            <HubIcon name="search" :size="22" />
          </button>
          <!-- Customers write in their orders' conversations; starting new ones is for organisations -->
          <button v-if="hub.me?.organisations.length" type="button" class="hub-icon-button" :aria-label="t('New conversation')" @click="showNew = true">
            <HubIcon name="pen" :size="22" />
          </button>
        </header>

        <!-- In the POS, the POS shows them over every view -->
        <HubIncidentBanner v-if="theme === 'app'" />

        <div v-if="searching" class="search">
          <input ref="searchInput" v-model="query" class="hub-field" type="search" :placeholder="t('Search conversations')" :aria-label="t('Search conversations')" />
        </div>

        <p v-if="!hub.online" class="hub-banner" role="status">
          {{
            unsent
              ? tn(unsent, "Offline. {n} message will send when the connection returns.", "Offline. {n} messages will send when the connection returns.")
              : t("Offline. Messages will send when the connection returns.")
          }}
        </p>

        <p v-if="bellFailed" class="hub-banner" role="alert">{{ t("Something went wrong.") }}</p>

        <nav v-if="manyOrgs" class="chips" :aria-label="t('Show conversations of')">
          <button type="button" class="chip on picker" :aria-haspopup="true" @click="picking = true">
            {{ filterOrg?.name ?? t("All") }}
            <HubIcon name="down" :size="18" />
          </button>
        </nav>
        <nav v-else-if="orgChips.length" class="chips" :aria-label="t('Show conversations of')">
          <button type="button" class="chip" :class="{ on: !hub.orgFilter }" @click="hub.orgFilter = null">{{ t("All") }}</button>
          <button
            v-for="o in orgChips"
            :key="o.id"
            type="button"
            class="chip"
            :class="{ on: hub.orgFilter === o.id }"
            @click="hub.orgFilter = o.id"
          >
            {{ o.name }}
          </button>
        </nav>

        <ul class="threads">
          <li v-for="thread in threads" :key="thread.id">
            <button
              type="button"
              class="thread-row"
              :class="{ active: thread.id === hub.openThreadId, unread: isUnread(thread) }"
              @click="hub.openThreadId = thread.id"
            >
              <HubAvatar v-bind="avatarProps(thread)" :size="44" />
              <span class="text">
                <strong>{{ threadTitle(thread) }}</strong>
                <span>{{ preview(thread) }}</span>
              </span>
              <span class="side">
                <span class="time">{{ time(thread) }}</span>
                <span v-if="isUnread(thread)" class="hub-badge" :aria-label="tn(unreadCount(thread), '{n} unread', '{n} unread')">
                  {{ unreadCount(thread) }}
                </span>
              </span>
            </button>
          </li>
          <template v-if="query && shownHits.length">
            <li class="section">{{ t("In messages") }}</li>
            <li v-for="hit in shownHits" :key="hit.messageId">
              <button type="button" class="thread-row hit" @click="openHit(hit)">
                <HubAvatar v-bind="avatarProps(hit.thread)" :size="44" />
                <span class="text">
                  <strong>{{ threadTitle(hit.thread) }}</strong>
                  <span>
                    <template v-for="(part, i) in snippetParts(hit.snippet)" :key="i">
                      <mark v-if="part.match">{{ part.text }}</mark><template v-else>{{ part.text.replace(/\s+/g, " ") }}</template>
                    </template>
                  </span>
                </span>
                <span class="side">
                  <span class="time">{{ when(hit.createdAt) }}</span>
                </span>
              </button>
            </li>
          </template>
          <li v-if="threads.length === 0 && !(query && shownHits.length)" class="empty">
            <template v-if="query">{{ t("Nothing matches “{query}”.", { query }) }}</template>
            <template v-else-if="hub.me?.organisations.length">{{ t("No conversations yet. Start one with the pen above.") }}</template>
            <template v-else>{{ t("Your conversations with shops about your orders show up here.") }}</template>
          </li>
        </ul>
      </section>

      <section class="thread-pane">
        <HubThreadView
          v-if="openThread"
          :key="openThread.id"
          :thread="openThread"
          :show-open-order="showOpenOrder"
          @close="hub.openThreadId = null"
          @open-order="(id: string) => emit('open-order', id)"
        >
          <template v-if="$slots['message-body']" #message-body="slot"><slot name="message-body" v-bind="slot" /></template>
        </HubThreadView>
        <div v-else class="placeholder">
          <HubIcon name="chat" :size="32" />
          <span>{{ t("Choose a conversation") }}</span>
        </div>
      </section>

      <HubOrgPicker
        v-if="picking"
        :title="t('Show conversations of')"
        :organisations="orgChips"
        :selected="hub.orgFilter"
        :all-label="t('All')"
        notifications
        @select="pickOrg"
        @close="picking = false"
      />
      <HubNewThread v-if="showNew" @close="showNew = false" @created="(id: string) => { showNew = false; hub.openThreadId = id }" />
    </div>
  </div>
</template>

<style scoped>
.hub-inbox {
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  container: hub-inbox / inline-size;
}

.inbox {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr;
}

/* One pane on a phone, the Fold's cover screen or a POS column; two from 600px (Android's medium width), e.g. the Fold opened. */
@container hub-inbox (min-width: 600px) {
  .inbox {
    grid-template-columns: minmax(300px, 38%) 1fr;
  }
  .thread-pane {
    border-left: 1px solid var(--hub-border);
  }
}

@container hub-inbox (max-width: 599px) {
  .inbox.thread-open .list-pane,
  .inbox:not(.thread-open) .thread-pane {
    display: none;
  }
}

.list-pane,
.thread-pane {
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.list-pane {
  background: var(--hub-surface);
}

.thread-pane {
  background: var(--hub-bg);
}

.top {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: max(12px, env(safe-area-inset-top)) max(8px, env(safe-area-inset-right)) 8px max(16px, env(safe-area-inset-left));
}

.title {
  flex: 1;
  min-width: 0;
  display: grid;
}

h1 {
  margin: 0;
  font-size: 1.6rem;
  line-height: 1.15;
}

.title span {
  color: var(--hub-muted);
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search {
  padding: 0 16px 10px;
}

.chips {
  display: flex;
  gap: 6px;
  padding: 0 16px 10px;
  overflow-x: auto;
  scrollbar-width: none;
  flex: none;
}

.chip {
  flex: none;
  border: 0;
  background: var(--hub-tint);
  border-radius: 16px;
  min-height: 32px;
  padding: 6px 14px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--hub-muted);
}

.chip.on {
  background: var(--hub-heading);
  color: var(--hub-surface);
}

.chip.picker {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding-right: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hub-icon-button.quiet {
  color: var(--hub-muted);
}

/* Edge to edge, the way Android lists go */
.threads {
  list-style: none;
  margin: 0;
  padding: 0 0 max(8px, env(safe-area-inset-bottom));
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  align-self: stretch;
}

.thread-row {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px max(16px, env(safe-area-inset-right)) 10px max(16px, env(safe-area-inset-left));
  border: 0;
  background: none;
  text-align: left;
  color: var(--hub-text);
  min-width: 0;
}

/* The line between rows starts at the text, past the avatar */
li + li .thread-row::before {
  content: "";
  position: absolute;
  top: 0;
  left: 74px;
  right: 0;
  border-top: 1px solid var(--hub-border);
}

.thread-row.active {
  background: var(--hub-tint);
}

.text {
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 1px;
}

.text strong {
  font-weight: 600;
  font-size: 0.97rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text span {
  color: var(--hub-muted);
  font-size: 0.88rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.unread .text strong {
  font-weight: 700;
}

.unread .text span {
  color: var(--hub-text);
}

.side {
  display: grid;
  justify-items: end;
  gap: 4px;
  align-self: flex-start;
  padding-top: 2px;
}

.time {
  color: var(--hub-muted);
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.empty {
  padding: 18px 16px;
  color: var(--hub-muted);
}

.section {
  padding: 14px 16px 4px;
  color: var(--hub-muted);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.hit mark {
  /* Johku's yellow, dark text: the same in light and dark */
  background: #effe00;
  color: #16191c;
  border-radius: 3px;
  padding: 0 2px;
}

.placeholder {
  margin: auto;
  display: grid;
  justify-items: center;
  gap: 8px;
  color: var(--hub-muted);
}

/* Inside the POS: the list fills the column like the POS's own lists */
.hub-theme-pos .top {
  padding: 12px 15px 10px;
}

.hub-theme-pos h1 {
  font-size: 1.4rem;
  letter-spacing: 0;
}

.hub-theme-pos .threads {
  border-top: 1px solid var(--hub-border);
}

.hub-theme-pos .thread-row {
  padding: 10px 15px;
}

.hub-theme-pos li + li .thread-row::before {
  left: 15px;
  right: 15px;
}

.hub-theme-pos .chip {
  background: #eceff1;
  border: 1px solid #cfd8dc;
  color: #333333;
}

.hub-theme-pos .chip.on {
  background: #455964;
  border-color: #455964;
  color: #ffffff;
}

.hub-theme-pos .chip {
  border-radius: 0;
}

.hub-theme-pos .hub-icon-button {
  border-radius: 0;
  background: #eceff1;
  border: 1px solid #cfd8dc;
}
</style>
